import React, {
  useState, useContext, createContext, useEffect,
} from 'react';
import PropTypes from 'prop-types';

import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { InteractionType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { getUser } from '../api/auth/GraphService';
import loginUser from '../api/services/Login';
import * as config from '../config/authConfig';

const userContext = createContext({
  user: undefined,
  error: undefined,
  signIn: undefined,
  signOut: undefined,
  displayError: undefined,
  clearError: undefined,
  authProvider: undefined,
});

export const useUserContext = () => useContext(userContext);

const useProvideUserContext = () => {
  const msal = useMsal();
  const [user, setUser] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [token, setToken] = useState(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(undefined);
  const [isCheckingUser, setIsCheckingUser] = useState(false);

  const displayError = (message, debug) => {
    setError({ message, debug });
  };

  const clearError = () => {
    setError(undefined);
  };

  const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
    msal.instance,
    {
      account: msal.accounts[0],
      scopes: config.loginRequest.scopes,
      interactionType: InteractionType.Redirect,
    },
  );

  const checkUser = async () => {
    if (!user) {
      if (!isCheckingUser) {
        try {
          await setIsCheckingUser(true);
          const account = await msal.accounts[0];
          if (account) {
            const currentUser = await getUser(authProvider);

            await setUser({
              displayName: currentUser.displayName || '',
              email: currentUser.mail || currentUser.userPrincipalName || '',
              userName: currentUser.userPrincipalName,
            });
            setIsCheckingUser(false);
          }
        } catch (err) {
          displayError(err.message);
          setIsCheckingUser(false);
        }
      }
    }
  };

  useEffect(() => {
    checkUser();
  });

  const checkToken = async () => {
    if (!token) {
      const account = await msal.accounts[0];
      if (account) {
        await msal.instance.acquireTokenSilent(
          {
            account,
            scopes: config.tokenRequest.scopes,
          },
        )
          .then((tokenRequest) => {
            setToken(tokenRequest.accessToken);
          });
      }
    } else if (!isAuthenticated) {
      loginUser(token, null);
      setIsAuthenticated(true);
    }
  };

  useEffect(() => {
    checkToken();
  }, []);

  const signIn = async () => {
    await msal.instance.loginRedirect({
      scopes: config.loginRequest.scopes,
      prompt: 'select_account',
    });
  };

  const signOut = async () => {
    await msal.instance.logoutRedirect();
    setUser(undefined);
  };

  return {
    user,
    error,
    signIn,
    signOut,
    displayError,
    clearError,
    authProvider,
    token,
    checkUser,
    checkToken,
    msal,
  };
};

const ProvideUserContext = ({ children }) => {
  const auth = useProvideUserContext();
  return (
    <userContext.Provider value={auth}>
      {children}
    </userContext.Provider>
  );
};

export default ProvideUserContext;

ProvideUserContext.propTypes = {
  children: PropTypes.element.isRequired,
};
